import React, { useState, useEffect, useRef } from 'react';
import { getUserCoins, UserCoins, supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { Coins, Clock, History } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CoinsDisplayProps {
  user: User | null;
  refreshTrigger?: number;
  showDetailed?: boolean;
  className?: string;
}

const CoinsDisplay = ({ 
  user, 
  refreshTrigger = 0, 
  showDetailed = false,
  className = ''
}: CoinsDisplayProps) => {
  const [userCoins, setUserCoins] = useState<UserCoins | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRecharge, setLastRecharge] = useState<string | null>(null);
  const [spentThisMonth, setSpentThisMonth] = useState<number>(0);
  const [isUpdated, setIsUpdated] = useState(false);
  const prevBalanceRef = useRef<number | null>(null);

  // Función para formatear números con separador de miles utilizando puntos
  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  useEffect(() => {
    if (user) {
      loadUserCoins();
      
      // Event listener para actualizar monedas cuando se dispara el evento personalizado
      const handleCoinsUpdated = () => {
        console.log('[COINS-DISPLAY] Evento coins-updated recibido, actualizando saldo...');
        loadUserCoins(true);
      };
      
      // Event listener para actualización previa (antes de la transacción)
      const handlePreCoinsUpdate = () => {
        console.log('[COINS-DISPLAY] Evento pre-coins-update recibido, preparando para actualización...');
        // Guardar el estado actual para comparación posterior
        if (userCoins) {
          prevBalanceRef.current = userCoins.balance;
        }
      };
      
      // Escuchar el evento correcto 'coins-updated'
      window.addEventListener('coins-updated', handleCoinsUpdated);
      
      // También escuchar el antiguo evento por compatibilidad
      window.addEventListener('coinsUpdated', handleCoinsUpdated);
      
      // Nuevo evento para sincronización previa
      window.addEventListener('pre-coins-update', handlePreCoinsUpdate);
      
      return () => {
        window.removeEventListener('coins-updated', handleCoinsUpdated);
        window.removeEventListener('coinsUpdated', handleCoinsUpdated);
        window.removeEventListener('pre-coins-update', handlePreCoinsUpdate);
      };
    }
  }, [user, refreshTrigger]);

  // Efecto para animar el cambio de saldo
  useEffect(() => {
    if (isUpdated) {
      const timer = setTimeout(() => {
        setIsUpdated(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isUpdated]);

  const loadUserCoins = async (forceUpdate = false) => {
    if (!user) return;
    
    setLoading(true);
    try {
      console.log(`[COINS-DISPLAY] Cargando monedas del usuario ${user.id}...`);
      const coins = await getUserCoins(user.id);
      
      if (coins) {
        console.log(`[COINS-DISPLAY] Monedas obtenidas:`, coins);
        
        // Verificar si el balance ha cambiado para activar la animación
        if (prevBalanceRef.current !== null && prevBalanceRef.current !== coins.balance) {
          console.log(`[COINS-DISPLAY] Cambio de balance detectado: ${prevBalanceRef.current} -> ${coins.balance}`);
          setIsUpdated(true);
        } else if (forceUpdate) {
          console.log(`[COINS-DISPLAY] Actualizando balance por fuerza (sin cambio detectado)`);
          setIsUpdated(true);
        }
        
        // Actualizar el valor anterior para la próxima comparación
        prevBalanceRef.current = coins.balance;
        
        // Actualizar el estado
        setUserCoins(coins);
        
        // Obtener la última recarga y gasto mensual
        try {
          const now = new Date();
          const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          
          const { data: recentTx, error: txError } = await supabase
            .from('coin_history')
            .select('*')
            .eq('user_id', user.id)
            .gt('amount', 0)
            .order('created_at', { ascending: false })
            .limit(1);
            
          if (!txError && recentTx && recentTx.length > 0) {
            const txDate = new Date(recentTx[0].created_at);
            setLastRecharge(txDate.toLocaleDateString());
          } else {
            setLastRecharge('No hay recargas');
          }
          
          // Calcular gasto del mes
          const { data: spentData, error: spentError } = await supabase
            .from('coin_history')
            .select('amount')
            .eq('user_id', user.id)
            .lt('amount', 0)
            .gte('created_at', firstDayOfMonth.toISOString());
            
          if (!spentError && spentData) {
            const totalSpent = spentData.reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
            setSpentThisMonth(totalSpent);
          }
        } catch (err) {
          console.error('[COINS-DISPLAY] Error al obtener datos de transacciones:', err);
        }
      } else {
        console.log(`[COINS-DISPLAY] No se encontraron monedas para el usuario ${user.id}`);
      }
    } catch (error) {
      console.error('[COINS-DISPLAY] Error al cargar monedas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Versión simple para uso en header
  if (!showDetailed) {
    if (loading || !user) {
      return (
        <div className={`flex items-center space-x-2 bg-gray-800/60 backdrop-blur-md px-3 py-1.5 rounded-full border border-gray-700/50 ${className}`}>
          <div className="w-4 h-4 bg-gray-600 rounded-full animate-pulse"></div>
          <span className="text-gray-400 text-sm">...</span>
        </div>
      );
    }

    return (
      <Link to="/coins" className={`flex items-center space-x-2 bg-gray-800/60 backdrop-blur-md px-3 py-1.5 rounded-full border ${isUpdated ? 'border-yellow-400 shadow-glow-yellow' : 'border-purple-500/30 hover:border-purple-500/50'} transition-all group cursor-pointer ${className}`}>
        <Coins className={`w-4 h-4 text-yellow-400 ${isUpdated ? 'animate-bounce' : 'group-hover:animate-bounce'}`} />
        <span className={`font-medium ${isUpdated ? 'text-yellow-300 animate-pulse' : 'text-purple-300'} text-sm`}>
          {formatNumber(userCoins?.balance || 0)}
        </span>
      </Link>
    );
  }
  
  // Versión detallada para dashboard
  if (loading || !user) {
    return (
      <div className={`bg-gray-800/80 rounded-lg shadow-lg border border-blue-500/20 p-5 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-700 rounded w-1/3"></div>
          <div className="h-10 bg-gray-700 rounded-lg w-1/2"></div>
          <div className="h-4 bg-gray-700 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-800/80 rounded-lg shadow-lg border ${isUpdated ? 'border-yellow-400/50 shadow-glow-yellow' : 'border-blue-500/20'} p-5 relative overflow-hidden transition-all duration-300 ${className}`}>
      {/* Efectos de fondo */}
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-yellow-500/5 rounded-full blur-xl"></div>
      <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>
      
      <div className="relative z-10">
        <div className="flex justify-between items-start">
          <div className="text-sm text-yellow-500 font-medium flex items-center gap-1.5">
            <Coins className={`w-4 h-4 ${isUpdated ? 'animate-bounce' : ''}`} />
            <span>Balance Actual</span>
          </div>
          
          <Link to="/coins" className="px-3 py-1 text-xs bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 rounded-full transition-all">
            Recargar
          </Link>
        </div>
        
        <div className={`mt-2 text-5xl font-bold ${isUpdated ? 'text-yellow-300 animate-pulse' : 'text-yellow-400'} transition-colors duration-300`}>
          {formatNumber(userCoins?.balance || 0)}
          <span className="text-xl ml-1 text-yellow-500/80">Monedas</span>
        </div>
        
        <div className="mt-5 grid grid-cols-2 gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-300">Última Recarga:</span>
            <span className="text-white font-medium ml-auto">{lastRecharge}</span>
          </div>
          
          <div className="flex items-center gap-1.5">
            <Coins className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-300">Gastado este mes:</span>
            <span className="text-white font-medium ml-auto">{spentThisMonth} Monedas</span>
          </div>
        </div>
        
        <div className="mt-5 pt-4 border-t border-gray-700">
          <Link 
            to="/transactions" 
            className="flex items-center justify-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            <History className="w-3.5 h-3.5" />
            <span>Ver historial de transacciones</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CoinsDisplay; 